<template>
  <div class="uti-flow-diagram">
    <div class="uti-flow-actions do-not-print">
      <el-button data-testid="print-flow-diagram" size="small" :plain="true" type="primary" @click="printDiagram">Print Diagram</el-button>
    </div>

    <svg
      ref="svg"
      class="uti-flow-svg"
      viewBox="0 0 1080 510"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      style="width:100%; max-width:1080px; height:auto; display:block; margin:1rem auto;"
    >
      <!-- Flow ribbons (drawn first, so boxes render on top) -->
      <path d="M 190 210 C 285 210 285 100 380 100" stroke="#a4d4b8" stroke-width="44" fill="none" />
      <path d="M 190 270 C 285 270 285 380 380 380" stroke="#e9b6a4" stroke-width="30" fill="none" />
      <path d="M 590 90  C 660 90  660 50  730 50"  stroke="#a4d4b8" stroke-width="36" fill="none" />
      <path d="M 590 170 C 660 170 660 150 730 150" stroke="#bfbfbf" stroke-width="10" fill="none" />
      <path d="M 590 320 C 660 320 660 320 730 320" stroke="#e9b6a4" stroke-width="18" fill="none" />
      <path d="M 590 380 C 660 380 660 420 730 420" stroke="#bfbfbf" stroke-width="18" fill="none" />

      <!-- Source node (blue) -->
      <g>
        <rect x="30" y="170" width="160" height="140" rx="6" fill="#e3edf7" />
        <rect x="30" y="170" width="7"   height="140"        fill="#3b75c3" />
        <foreignObject x="48" y="180" width="135" height="120">
          <div xmlns="http://www.w3.org/1999/xhtml" style="height:100%; display:flex; flex-direction:column; justify-content:center; color:#2c5a91;">
            <div style="font-size:22px; font-weight:700;">{{ sourceValue }}</div>
            <div style="font-size:13px; color:#3b75c3; margin-top:4px;">{{ sourceLabel }}</div>
          </div>
        </foreignObject>
      </g>

      <!-- Criteria met node (green) -->
      <g>
        <rect x="380" y="60" width="210" height="140" rx="6" fill="#d9ede2" />
        <rect x="380" y="60" width="7"   height="140"        fill="#2d9670" />
        <foreignObject x="398" y="70" width="185" height="120">
          <div xmlns="http://www.w3.org/1999/xhtml" style="height:100%; display:flex; flex-direction:column; justify-content:center; color:#2d9670;">
            <div style="font-size:20px; font-weight:700;">{{ metValue }}</div>
            <div style="font-size:13px; margin-top:4px;">{{ metLabel }}</div>
          </div>
        </foreignObject>
      </g>

      <!-- Criteria not met node (orange/red) -->
      <g>
        <rect x="380" y="280" width="210" height="140" rx="6" fill="#f5dcd4" />
        <rect x="380" y="280" width="7"   height="140"        fill="#d4654a" />
        <foreignObject x="398" y="290" width="185" height="120">
          <div xmlns="http://www.w3.org/1999/xhtml" style="height:100%; display:flex; flex-direction:column; justify-content:center; color:#b14a30;">
            <div style="font-size:20px; font-weight:700;">{{ notMetValue }}</div>
            <div style="font-size:13px; margin-top:4px;">{{ notMetLabel }}</div>
          </div>
        </foreignObject>
      </g>

      <!-- Outcome: action taken, criteria met (green) -->
      <g>
        <rect x="730" y="10" width="320" height="80" rx="6" fill="#d9ede2" />
        <rect x="730" y="10" width="7"   height="80"        fill="#2d9670" />
        <foreignObject x="748" y="20" width="295" height="65">
          <div xmlns="http://www.w3.org/1999/xhtml" style="height:100%; display:flex; align-items:center; gap:0.4em; color:#2d9670; font-size:14px; font-weight:600;">
            <span>{{ actionMetValue }}</span>
            <span>{{ actionMetLabel }}</span>
          </div>
        </foreignObject>
      </g>

      <!-- Outcome: no action, criteria met (gray) -->
      <g>
        <rect x="730" y="110" width="320" height="80" rx="6" fill="#ececec" />
        <rect x="730" y="110" width="7"   height="80"        fill="#7c7c7c" />
        <foreignObject x="748" y="120" width="295" height="65">
          <div xmlns="http://www.w3.org/1999/xhtml" style="height:100%; display:flex; align-items:center; gap:0.4em; color:#555; font-size:14px; font-weight:600;">
            <span>{{ noActionMetValue }}</span>
            <span>{{ noActionMetLabel }}</span>
          </div>
        </foreignObject>
      </g>

      <!-- Compliance Alert legend pill -->
      <g>
        <rect x="730" y="232" width="220" height="28" rx="4" fill="#fdecc8" />
        <foreignObject x="740" y="237" width="205" height="22">
          <div xmlns="http://www.w3.org/1999/xhtml" style="display:flex; align-items:center; gap:0.4em; color:#8a6d3b; font-size:12px; font-weight:600;">
            {{ legendFailure }}
          </div>
        </foreignObject>
      </g>

      <!-- Outcome: action taken, no criteria (orange/red) -->
      <g>
        <rect x="730" y="280" width="320" height="80" rx="6" fill="#f5dcd4" />
        <rect x="730" y="280" width="7"   height="80"        fill="#d4654a" />
        <foreignObject x="748" y="290" width="295" height="65">
          <div xmlns="http://www.w3.org/1999/xhtml" style="height:100%; display:flex; align-items:center; gap:0.4em; color:#b14a30; font-size:14px; font-weight:600;">
            <span>{{ actionNotMetValue }}</span>
            <span>{{ actionNotMetLabel }}</span>
          </div>
        </foreignObject>
      </g>

      <!-- Outcome: no action, no criteria (gray) -->
      <g>
        <rect x="730" y="380" width="320" height="80" rx="6" fill="#ececec" />
        <rect x="730" y="380" width="7"   height="80"        fill="#7c7c7c" />
        <foreignObject x="748" y="390" width="295" height="65">
          <div xmlns="http://www.w3.org/1999/xhtml" style="height:100%; display:flex; align-items:center; gap:0.4em; color:#555; font-size:14px; font-weight:600;">
            <span>{{ noActionNotMetValue }}</span>
            <span>{{ noActionNotMetLabel }}</span>
          </div>
        </foreignObject>
      </g>

      <!-- Edge labels (numbers along the flow ribbons) -->
      <foreignObject x="250" y="125" width="50" height="22">
        <div xmlns="http://www.w3.org/1999/xhtml" style="color:#2d9670; font-size:13px; font-weight:600;">{{ edgeSourceMet }}</div>
      </foreignObject>
      <foreignObject x="250" y="340" width="50" height="22">
        <div xmlns="http://www.w3.org/1999/xhtml" style="color:#d4654a; font-size:13px; font-weight:600;">{{ edgeSourceNotMet }}</div>
      </foreignObject>
      <foreignObject x="630" y="40" width="50" height="22">
        <div xmlns="http://www.w3.org/1999/xhtml" style="color:#2d9670; font-size:13px; font-weight:600;">{{ edgeMetAction }}</div>
      </foreignObject>
      <foreignObject x="630" y="175" width="50" height="22">
        <div xmlns="http://www.w3.org/1999/xhtml" style="color:#7c7c7c; font-size:13px; font-weight:600;">{{ edgeMetNoAction }}</div>
      </foreignObject>
      <foreignObject x="630" y="290" width="50" height="22">
        <div xmlns="http://www.w3.org/1999/xhtml" style="color:#d4654a; font-size:13px; font-weight:600;">{{ edgeNotMetAction }}</div>
      </foreignObject>
      <foreignObject x="630" y="405" width="50" height="22">
        <div xmlns="http://www.w3.org/1999/xhtml" style="color:#7c7c7c; font-size:13px; font-weight:600;">{{ edgeNotMetNoAction }}</div>
      </foreignObject>

      <!-- Footer: compliance formula -->
      <foreignObject x="20" y="478" width="900" height="28">
        <div xmlns="http://www.w3.org/1999/xhtml" style="color:#555; font-size:13px;">{{ formula }}</div>
      </foreignObject>
    </svg>
  </div>
</template>

<script>
// Styles for the print document. The diagram is 1080x510, so landscape wastes the
// least paper. print-color-adjust keeps the node fills, which browsers drop by default.
const PRINT_STYLES = `
  @page {
    size: landscape;
    margin: 12mm;
  }

  body {
    margin: 0;
    font-family: "Open Sans", "Helvetica Neue", Arial, sans-serif;
    color: #333;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  h1 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 12px;
  }

  svg {
    display: block !important;
    width: 100% !important;
    max-width: none !important;
    height: auto !important;
    margin: 0 !important;
  }
`;

function escapeHtml(value) {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export default {
  name: "flow-diagram",
  props: {
    // Names the print document. Browsers use it for the "Save as PDF" file name.
    title: { type: String, default: "Infection flow" },
    sourceValue: { type: [String, Number], default: "" },
    sourceLabel: { type: String, default: "" },
    metValue: { type: [String, Number], default: "" },
    metLabel: { type: String, default: "" },
    notMetValue: { type: [String, Number], default: "" },
    notMetLabel: { type: String, default: "" },
    actionMetValue: { type: [String, Number], default: "" },
    actionMetLabel: { type: String, default: "" },
    noActionMetValue: { type: [String, Number], default: "" },
    noActionMetLabel: { type: String, default: "" },
    actionNotMetValue: { type: [String, Number], default: "" },
    actionNotMetLabel: { type: String, default: "" },
    noActionNotMetValue: { type: [String, Number], default: "" },
    noActionNotMetLabel: { type: String, default: "" },
    edgeSourceMet: { type: [String, Number], default: "" },
    edgeSourceNotMet: { type: [String, Number], default: "" },
    edgeMetAction: { type: [String, Number], default: "" },
    edgeMetNoAction: { type: [String, Number], default: "" },
    edgeNotMetAction: { type: [String, Number], default: "" },
    edgeNotMetNoAction: { type: [String, Number], default: "" },
    legendFailure: { type: String, default: "" },
    formula: { type: String, default: "" },
  },
  methods: {
    // Print only the diagram. The browser print dialog also saves it as a PDF,
    // so no PDF library is necessary.
    printDiagram() {
      const svg = this.$refs.svg;
      if (!svg) {
        return;
      }

      const printWindow = window.open("", "_blank", "width=1120,height=720");
      if (!printWindow) {
        this.$message.error("Allow pop-up windows for this site to print the diagram.");
        return;
      }

      const markup = new XMLSerializer().serializeToString(svg);
      const heading = escapeHtml(this.title);
      const printDocument = printWindow.document;

      printDocument.open();
      printDocument.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${heading}</title><style>${PRINT_STYLES}</style></head><body><h1>${heading}</h1>${markup}</body></html>`);
      printDocument.close();

      printWindow.onafterprint = () => printWindow.close();

      const openPrintDialog = () => {
        printWindow.focus();
        printWindow.print();
      };

      if (printDocument.readyState === "complete") {
        // Give the new document one tick to lay the diagram out before it prints.
        printWindow.setTimeout(openPrintDialog, 0);
      } else {
        printWindow.addEventListener("load", openPrintDialog);
      }
    },
  },
};
</script>

<style scoped>
.uti-flow-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
